import React, { useCallback, useRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import uuid from 'uuid';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import InlineDialog from '@atlaskit/inline-dialog';
import { token } from '@atlaskit/tokens';
import {
	AnalyticsEventToProps,
	fireUIAnalytics,
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useManuallyCloseOnOutsideClick } from './controllers/manually-close-on-outside-click';
import { useActiveUpFlowTouchpointId } from './services';
import type { Props } from './types';
import { DialogContent } from './ui/dialog-content/async.tsx';

export const UpflowTouchpoint = (props: Props) => {
	const {
		internationalizedMessage,
		placement,
		onClose,
		onOpen,
		feature,
		touchpointId,
		isOpen,
		children,
		actionSubjectId,
		shouldManuallyCloseOnOutsideClick = false,
	} = props;

	// (UX) Store is used to monitor active touchpoint, so other touchpoints
	// know to force close if another opens
	const [activeUpFlowTouchpointId, { setActiveUpFlowTouchpointId }] = useActiveUpFlowTouchpointId();
	const internalId = useRef(uuid.v4());

	const openAndUpdateStore = useCallback(() => {
		setActiveUpFlowTouchpointId(internalId.current);
		onOpen();
	}, [onOpen, setActiveUpFlowTouchpointId]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCloseWithAnalytics = useCallback(() => {
		const event = createAnalyticsEvent({});
		fireOperationalAnalytics(event, 'upflowTouchpoint hidden', { touchpointId });
		onClose();
	}, [createAnalyticsEvent, onClose, touchpointId]);

	const closeAndUpdateStore = useCallback(() => {
		setActiveUpFlowTouchpointId('');
		onCloseWithAnalytics();
	}, [onCloseWithAnalytics, setActiveUpFlowTouchpointId]);

	useEffect(() => {
		if (activeUpFlowTouchpointId !== internalId.current && isOpen) {
			onCloseWithAnalytics();
		}
	}, [activeUpFlowTouchpointId, isOpen, onCloseWithAnalytics]);

	const onMouseEnter = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			// fire analytics only when opening
			if (isOpen === false) {
				// Flow is dumb that it complains if you directly gives actionSubjectId. It doesn't know actionSubjectId is always string
				fireUIAnalytics(analyticsEvent, `${actionSubjectId}`);
			}
			openAndUpdateStore();
		},
		[actionSubjectId, isOpen, openAndUpdateStore],
	);

	const { dialogContentRef, triggerRef } = useManuallyCloseOnOutsideClick({
		isOpen,
		onClose: closeAndUpdateStore,
		shouldManuallyCloseOnOutsideClick,
	});

	const dialogContent = (
		<DialogContent
			feature={feature}
			touchpointId={touchpointId}
			internationalizedMessage={internationalizedMessage}
			onUpFlowDismiss={closeAndUpdateStore}
			isOpen={isOpen}
		/>
	);

	return (
		<InlineDialog
			content={
				shouldManuallyCloseOnOutsideClick ? (
					<div ref={dialogContentRef}>{dialogContent}</div>
				) : (
					dialogContent
				)
			}
			placement={placement}
			isOpen={isOpen}
			onClose={closeAndUpdateStore}
		>
			<DivWithMouseEnterAnalytics
				onMouseEnter={onMouseEnter}
				ref={shouldManuallyCloseOnOutsideClick ? triggerRef : undefined}
			>
				{children}
			</DivWithMouseEnterAnalytics>
		</InlineDialog>
	);
};

export default UpflowTouchpoint;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MouseEnterWrapper = styled.div({
	padding: token('space.0', '0px'),
});

const DivWithMouseEnterAnalytics = AnalyticsEventToProps('tooltip', {
	onMouseEnter: 'displayed',
})(MouseEnterWrapper);
